import { useState, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import { documentRemoveNotes, documentUpdateNotes, documentRemoveNotesInList } from 'domain/documents';
import { promisify } from 'lib/helpers';

import toast from 'components/Toast';

import { validateNotePaletteKey } from 'components/LabeledThemeProvider/notesPalette';

const useStickyNotes = ({
  onClose = () => {},
  closeAfterSave = false,
  content = '',
  noteColorKey = '',
  documentID,
}) => {
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(false);
  const [value, setValue] = useState(content);

  const defaultValidNotePaletteKey = useMemo(() => validateNotePaletteKey(noteColorKey), [noteColorKey]);

  const [colorKey, setColorKey] = useState(defaultValidNotePaletteKey);

  const handleChange = useCallback((e) => {
    setValue(e.currentTarget.value);
  }, []);

  const handleChangeRadio = useCallback((event) => {
    setColorKey(event.target.value);
  }, []);

  const onNotesUpdate = useCallback(() => {
    setLoading(true);
    new Promise((resolve, reject) => {
      dispatch(documentUpdateNotes({ notes: value, notesColor: colorKey, documentID, cb: { resolve, reject } }));
    })
      .then(() => {
        toast.info(
          formatMessage({
            id: 'document.actions.notes.success',
            defaultMessage: 'Documents notes were updated',
          }),
        );
      })
      .catch()
      .finally(() => {
        setLoading(false);
        if (closeAfterSave) onClose();
      });
  }, [formatMessage, value, colorKey, dispatch, onClose, closeAfterSave, documentID]);

  const onNotesRemove = useCallback(() => {
    setLoading(true);
    const fn = (arg) =>
      dispatch(documentID ? documentRemoveNotesInList({ documentID, ...arg }) : documentRemoveNotes({ ...arg }));
    promisify(fn, { notes: value })
      .then(() => {
        toast.info(
          formatMessage({
            id: 'document.actions.notesRemove.success',
            defaultMessage: 'Documents notes were removed',
          }),
        );
      })
      .catch()
      .finally(() => {
        setLoading(false);
        onClose();
      });
  }, [formatMessage, dispatch, value, onClose, documentID]);

  const disableUpdate = useMemo(
    () => !value || value.length < 1 || (content === value && noteColorKey === colorKey) || loading,
    [value, loading, content, noteColorKey, colorKey],
  );

  return {
    value,
    setValue,
    colorKey,
    setColorKey,
    defaultValidNotePaletteKey,
    loading,
    handleChange,
    handleChangeRadio,
    onNotesUpdate,
    onNotesRemove,
    disableUpdate,
  };
};

export default useStickyNotes;
